import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Lesson, Achievement } from "../../types";
import { fetchLessons, completeLesson } from "../../utils/api";
import { AppHeader, Banner, StatusBadge, Icon } from "../common/ui";
import ExercisePanel from "./ExercisePanel";
import LessonContent, { hasVideoBlock } from "./LessonContent";
import LessonVideo from "./LessonVideo";
import { useAppConfig } from "../../config/AppConfigContext";
import PrintTemplates, { PrintTemplatesRow } from "./PrintTemplates";
import LessonActions from "./LessonActions";

interface Props {
  token: string;
  onExit: () => void;
  theme: "dark" | "light";
  onThemeChange: (t: "dark" | "light") => void;
  initialOpenId?: string | null;
  /** When the lesson was deep-linked from elsewhere (e.g. the calculator's
   *  result), back should return there instead of dropping into the list. */
  onBackFromLesson?: (() => void) | null;
  onAsk: (subject: string, lessonId: string) => void;
  /** Locked lesson / locked task → the plans page, remembering the chapter. */
  onOpenPlans?: (section?: string | null) => void;
  /** Chapter to expand on mount — set when coming back from the plans page. */
  initialOpenSection?: string | null;
  isAdmin?: boolean;
  onQuizStats?: () => void;
  onAchievement?: (a: Achievement) => void;
}

function LessonBadges({ lesson }: { lesson: Lesson }) {
  const { t } = useTranslation();
  const hasExercises = lesson.exercises && lesson.exercises.length > 0;
  const exercisePassed = hasExercises && lesson.exercises.every((ex) => ex.submission?.status === "passed");
  const exerciseCount = lesson.exercises?.length ?? 0;
  const passedCount = lesson.exercises?.filter((ex) => ex.submission?.status === "passed").length ?? 0;
  return (
    <div className="row" style={{ gap: 7, flexWrap: "wrap" }}>
      {/* Open but not submittable = the chapter's demo lesson; open AND
          submittable = a subscriber, so nothing needs saying. */}
      {lesson.locked
        ? <span className="badge"><Icon name="lock" size={11} /> {t("study.locked")}</span>
        : lesson.can_submit === false && lesson.owns_course === false
          ? <span className="badge badge-success"><Icon name="lockOpen" size={11} /> {t("study.demo")}</span>
          : <span className="badge badge-success"><Icon name="lockOpen" size={11} /> {t("study.available")}</span>}
      <StatusBadge status={lesson.status} />
      {hasExercises && (
        <span className={`badge ${exercisePassed ? "badge-success" : "badge-violet"}`}>
          <Icon name="brush" size={11} />
          {` ${passedCount}/${exerciseCount}`}
        </span>
      )}
    </div>
  );
}

/** Compact navigation card — tapping it opens the lesson on its own page, or
 *  the plans page when the lesson is behind the subscription. */
function LessonCard({ lesson, onOpen, onOpenPlans }: {
  lesson: Lesson; onOpen: () => void; onOpenPlans?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const isRu = !i18n.language?.startsWith("en");
  const title = isRu ? lesson.title_ru : (lesson.title_en || lesson.title_ru);
  // A locked card is a sales surface, not a dead end — unless there is nowhere
  // to send the user, in which case it stays inert as before.
  const toPlans = lesson.locked && !!onOpenPlans;
  const inert = lesson.locked && !onOpenPlans;

  return (
    <div className="glass glass-lg glass-sheen" style={{ overflow: "hidden", opacity: lesson.locked ? 0.62 : 1 }}>
      <button
        onClick={() => (lesson.locked ? onOpenPlans?.() : onOpen())}
        disabled={inert}
        style={{
          width: "100%", textAlign: "left", background: "transparent", border: "none",
          color: "inherit", cursor: inert ? "not-allowed" : "pointer",
          padding: 16, display: "flex", gap: 13, alignItems: "center",
        }}
      >
        <span style={{
          width: 42, height: 42, borderRadius: 12, flex: "none", display: "grid", placeItems: "center",
          fontWeight: 800, fontSize: "1rem",
          background: lesson.locked ? "var(--glass-1)" : "var(--violet-tint)",
          color: lesson.locked ? "var(--text-3)" : "var(--violet-text)",
        }}>
          {lesson.locked ? <Icon name="lock" size={19} /> : lesson.order}
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ marginBottom: 4 }}><LessonBadges lesson={lesson} /></div>
          <div className="t-h" style={{ lineHeight: 1.2 }}>{title}</div>
          {toPlans && (
            <div className="t-xs" style={{ marginTop: 4, color: "var(--violet-text)", fontWeight: 700 }}>
              {t("study.unlock")}
            </div>
          )}
        </div>

        {!inert && (
          <span style={{ flex: "none", color: "var(--text-3)" }}>
            <Icon name="chevronRight" size={20} />
          </span>
        )}
      </button>
    </div>
  );
}

/** Full lesson page — own scroll container, opened from the list. */
function LessonPage({
  lesson, token, onBack, onAsk, onOpened, onOpenPlans, theme, onThemeChange,
}: {
  lesson: Lesson;
  token: string;
  onBack: () => void;
  onAsk: (subject: string, lessonId: string) => void;
  onOpened: (l: Lesson) => void;
  onOpenPlans?: () => void;
  theme: "dark" | "light";
  onThemeChange: (t: "dark" | "light") => void;
}) {
  const { t, i18n } = useTranslation();
  const isRu = !i18n.language?.startsWith("en");

  const title = isRu ? lesson.title_ru : (lesson.title_en || lesson.title_ru);
  const desc = isRu ? lesson.description_ru : (lesson.description_en || lesson.description_ru);
  const hasExercises = lesson.exercises && lesson.exercises.length > 0;

  // Reading-only lessons complete when the reader reaches the END of the page
  // (the «Урок завершён!» pop-up used to fire the moment the lesson opened).
  // A lesson short enough to fit on screen is read at a glance, so it counts.
  const scrollRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const end = endRef.current;
    if (!end || typeof IntersectionObserver === "undefined") { onOpened(lesson); return; }
    let done = false;
    const io = new IntersectionObserver((entries) => {
      if (done || !entries.some((e) => e.isIntersecting)) return;
      done = true;
      io.disconnect();
      onOpened(lesson);
    }, { root: scrollRef.current, threshold: 0 });
    io.observe(end);
    return () => io.disconnect();
  }, [lesson.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const placesOwnVideo = hasVideoBlock(lesson.content);
  const video = lesson.has_video ? (
    <LessonVideo lessonId={lesson.id} isRu={isRu} token={token} startPosition={lesson.video_position ?? 0} />
  ) : lesson.video_url ? (
    <a href={lesson.video_url} target="_blank" rel="noreferrer"
      className="btn btn-glass btn-block" style={{ textDecoration: "none", marginBottom: 16 }}>
      <Icon name="play" size={18} /> {t("selection.watchVideo")}
    </a>
  ) : null;

  return (
    <div className="beeapp__scroll" ref={scrollRef}>
      <AppHeader theme={theme} onThemeChange={onThemeChange} onBack={onBack} title={title} showLang={false} />

      <div className="wrap content-cap" style={{ paddingTop: 6, maxWidth: 720 }}>
        <div className="anim-rise" style={{ marginBottom: 16 }}>
          <LessonBadges lesson={lesson} />
        </div>

        {/* Lesson description */}
        <p className="t-sm muted" style={{ fontWeight: 600, lineHeight: 1.5, marginBottom: 18 }}>{desc}</p>

        {/* Structured lesson content. The video is placed by the lesson's own
            `video` marker block — description first, then the video, then the
            numbered steps. Lessons without a marker keep the old order (video
            after the text), so nothing breaks while content is migrated. */}
        <LessonContent content={lesson.content} isRu={isRu} videoSlot={video} />
        {!placesOwnVideo && video}

        {/* The printable practice sheets, kept within reach of the exercise.
            Paper chapter only — that is the chapter they are used in, and the
            expert asked for them nowhere else. */}
        {lesson.section === PAPER && <PrintTemplatesRow />}

        {/* Exercise panels */}
        {hasExercises && (
          <div className="stack" style={{ "--gap": "10px", marginBottom: 16 } as React.CSSProperties}>
            {lesson.exercises.map((ex) => (
              <ExercisePanel
                key={ex.id} exercise={ex} token={token} isRu={isRu}
                canSubmit={lesson.can_submit !== false}
                // Without the tutor block the lesson-level card below explains
                // it once; a lock in every task panel would repeat it.
                explainLock={lesson.can_ask !== false}
                // With a single task the brief is read here and sent from the
                // paired buttons below, so this panel would be a second copy
                // of the same button.
                hideSubmit={lesson.exercises.length === 1}
                onOpenPlans={onOpenPlans}
              />
            ))}
          </div>
        )}

        {/* Send work in, or ask about the lesson — the two ways to reach Anya,
            both sold as the feedback block. */}
        <LessonActions
          lesson={lesson}
          title={title}
          token={token}
          onAsk={onAsk}
          onOpenPlans={onOpenPlans}
        />
        {/* The end of the lesson: reaching it completes a reading-only lesson. */}
        <div ref={endRef} aria-hidden style={{ height: 1 }} />
      </div>
    </div>
  );
}

/** Collapsible chapter header — sections start minimized; tap to expand. */
function SectionHeader({
  label, done, total, open, comingSoon, onToggle,
}: {
  label: string; done: number; total: number; open: boolean; comingSoon: boolean; onToggle: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      onClick={() => !comingSoon && onToggle()}
      disabled={comingSoon}
      aria-expanded={open}
      className="glass glass-lg"
      style={{
        width: "100%", border: "none", color: "inherit", cursor: comingSoon ? "default" : "pointer",
        padding: "14px 16px", marginTop: 14, display: "flex", alignItems: "center", gap: 12,
        opacity: comingSoon ? 0.6 : 1,
      }}
    >
      <span className="t-sm" style={{
        fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase",
        color: comingSoon ? "var(--text-3)" : "var(--violet-text)", flex: "none",
      }}>{label}</span>
      <span style={{ flex: 1 }} />
      {comingSoon ? (
        <span className="badge"><Icon name="clock" size={11} /> {t("study.comingSoon")}</span>
      ) : (
        <>
          <span className={`badge ${done === total && total > 0 ? "badge-success" : "badge-violet"}`}>
            {done}/{total}
          </span>
          <span style={{
            flex: "none", color: "var(--text-3)",
            transition: "transform 0.3s var(--ease-soft)",
            transform: open ? "rotate(180deg)" : "none",
          }}>
            <Icon name="chevronDown" size={18} />
          </span>
        </>
      )}
    </button>
  );
}

// The chapter the printable sheets belong to.
const PAPER = "paper";

// Chapters grouped into separately-sold courses. Must mirror COURSE_SECTIONS in
// backend/app/schemas/settings.py — the server is what actually enforces access.
// Practical track first (placeholders until filled), reference theory last.
const COURSES: { key: "base" | "ombre"; sections: { key: string; placeholder: boolean }[] }[] = [
  {
    key: "base",
    sections: [
      { key: "paper", placeholder: true },
      { key: "strokes", placeholder: true },
      { key: "schemes", placeholder: true },
    ],
  },
  {
    key: "ombre",
    sections: [
      { key: "selection", placeholder: false },
      { key: "theory", placeholder: false },
    ],
  },
];

/** Which course a chapter belongs to; the intro lesson sits in the base course. */
function courseOf(section: string | null | undefined): "base" | "ombre" {
  return COURSES.find((c) => c.sections.some((x) => x.key === section))?.key ?? "base";
}

/** Banner for a course that is sold separately and is not open yet. */
function ClosedCourse({ courseKey, lessons }: { courseKey: string; lessons: number }) {
  const { t } = useTranslation();
  return (
    <div className="glass glass-lg" style={{ padding: 18, marginTop: 14, opacity: 0.78 }}>
      <div className="row" style={{ gap: 11 }}>
        <span style={{
          display: "grid", placeItems: "center", width: 38, height: 38, borderRadius: 11,
          background: "var(--glass-1)", color: "var(--text-3)", flex: "none",
        }}>
          <Icon name="lock" size={19} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <span className="t-h">{t(`study.course_${courseKey}`)}</span>
            <span className="badge"><Icon name="clock" size={11} /> {t("study.comingSoon")}</span>
          </div>
          <div className="t-xs muted-2" style={{ marginTop: 3, lineHeight: 1.4 }}>
            {t("study.courseClosedHint", { count: lessons })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function StudyView({ token, onExit, theme, onThemeChange, initialOpenId, onBackFromLesson, onAsk, onOpenPlans, initialOpenSection, isAdmin, onQuizStats, onAchievement }: Props) {
  const { t, i18n } = useTranslation();
  const { config } = useAppConfig();
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(initialOpenId ?? null);
  // Chapters start minimized; the user expands the one they're interested in.
  const [openSections, setOpenSections] = useState<Set<string>>(
    () => new Set(initialOpenSection ? [initialOpenSection] : []),
  );
  const toggleSection = (k: string) => setOpenSections((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  useEffect(() => {
    fetchLessons(token)
      .then(setLessons)
      .catch((e) => setError(e instanceof Error ? e.message : t("common.error")))
      .finally(() => setLoading(false));
  }, [token, t]);

  // Progress counts only what the student can actually reach: a closed course
  // in the denominator would peg everyone's percentage down for lessons that
  // are not on sale yet.
  const counted = lessons.filter((l) => isAdmin || config.courses?.[courseOf(l.section)] !== false);
  const doneCount = counted.filter((l) => l.status === "done" || l.status === "reviewed").length;
  const total = counted.length;

  // Count total exercises and passed
  const totalEx = counted.reduce((n, l) => n + (l.exercises?.length ?? 0), 0);
  const passedEx = counted.reduce((n, l) => n + (l.exercises?.filter((e) => e.submission?.status === "passed").length ?? 0), 0);

  const introLessons = lessons.filter((l) => !l.section);

  // Opening a reading-only lesson (no exercises) marks it done and celebrates once.
  const markOpened = (l: Lesson) => {
    const hasExercises = (l.exercises?.length ?? 0) > 0;
    if (hasExercises || l.status === "done") return;
    completeLesson(l.id, token).then((r) => {
      if (!r.newly_completed) return;
      setLessons((prev) => prev.map((x) => (x.id === l.id ? { ...x, status: "done" } : x)));
      const isRu = !i18n.language?.startsWith("en");
      const title = isRu ? l.title_ru : (l.title_en || l.title_ru);
      onAchievement?.({ id: `lesson-done-${l.id}`, variant: "success", title: t("achv.lessonDoneTitle"), message: title });
    }).catch(() => { /* non-blocking */ });
  };

  const openLesson = openId ? lessons.find((l) => l.id === openId && !l.locked) : undefined;
  if (openLesson) {
    return (
      <LessonPage
        lesson={openLesson}
        token={token}
        onBack={() => {
          // Deep-linked in from another screen? Hand back to whoever opened us.
          if (onBackFromLesson && openId === initialOpenId) { onBackFromLesson(); return; }
          // Otherwise land on the list with this lesson's chapter expanded.
          const sec = openLesson.section;
          if (sec) setOpenSections((prev) => new Set(prev).add(sec));
          setOpenId(null);
        }}
        onAsk={onAsk}
        onOpened={markOpened}
        onOpenPlans={onOpenPlans ? () => onOpenPlans(openLesson.section) : undefined}
        theme={theme}
        onThemeChange={onThemeChange}
      />
    );
  }

  const renderCard = (l: Lesson) => (
    <LessonCard
      key={l.id}
      lesson={l}
      onOpen={() => setOpenId(l.id)}
      onOpenPlans={onOpenPlans ? () => onOpenPlans(l.section) : undefined}
    />
  );

  return (
    <div className="beeapp__scroll">
      <AppHeader theme={theme} onThemeChange={onThemeChange} onBack={onExit} title={t("study.title")} />

      <div className="wrap content-cap" style={{ paddingTop: 6, maxWidth: 880 }}>
        {/* Progress header */}
        <div className="glass glass-lg anim-rise" style={{ padding: 18, marginBottom: 18 }}>
          <div className="between" style={{ marginBottom: 12 }}>
            <div className="row" style={{ gap: 11 }}>
              <span style={{
                display: "grid", placeItems: "center", width: 44, height: 44, borderRadius: 13,
                background: "linear-gradient(160deg, var(--violet-fill), var(--violet-fill-2))",
                color: "var(--violet-ink)", flex: "none",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4)",
              }}>
                <Icon name="graduation" size={24} />
              </span>
              <div>
                <div className="t-h">{t("study.progress")}</div>
                <div className="t-sm muted">
                  {loading ? t("common.loading") : `${doneCount} / ${total} ${t("study.lessons")}`}
                </div>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="t-display" style={{ color: "var(--violet-text)" }}>
                {loading ? "—" : `${Math.round((doneCount / (total || 1)) * 100)}%`}
              </div>
              {!loading && totalEx > 0 && (
                <div className="t-xs muted-2">
                  {passedEx}/{totalEx} {t("study.exercises")}
                </div>
              )}
            </div>
          </div>
          <div className="progress">
            <div className="progress__fill" style={{
              width: loading ? "0%" : `${(doneCount / (total || 1)) * 100}%`,
              background: "linear-gradient(90deg, var(--violet-fill-2), var(--violet-fill))",
              boxShadow: "0 0 12px var(--violet-glow)",
            }} />
          </div>
        </div>

        {/* Admin: quiz questions & answer statistics */}
        {isAdmin && onQuizStats && (
          <button className="glass glass-lg" onClick={onQuizStats}
            style={{ width: "100%", border: "none", color: "inherit", cursor: "pointer", padding: "14px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ display: "grid", placeItems: "center", width: 38, height: 38, borderRadius: 11, flex: "none", background: "var(--accent-tint)", color: "var(--accent-text)" }}>
              <Icon name="trophy" size={19} />
            </span>
            <div style={{ flex: 1, textAlign: "left" }}>
              <div className="t-h">{t("quizStats.title")}</div>
              <div className="t-xs muted-2">{t("quizStats.menuDesc")}</div>
            </div>
            <Icon name="chevronRight" size={18} />
          </button>
        )}

        {error && <div style={{ marginBottom: 16 }}><Banner kind="error">{error}</Banner></div>}

        {loading ? (
          <div className="stack" style={{ "--gap": "12px" } as React.CSSProperties}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="glass glass-lg" style={{ padding: 16, display: "flex", gap: 13, alignItems: "center" }}>
                <div className="skel" style={{ width: 42, height: 42, borderRadius: 12 }} />
                <div style={{ flex: 1 }}>
                  <div className="skel" style={{ width: 90, height: 14, marginBottom: 8 }} />
                  <div className="skel" style={{ width: "70%", height: 16 }} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Intro (no section) renders first, headerless */}
            {introLessons.length > 0 && <div className="grid-2 stagger" style={{ alignItems: "start" }}>{introLessons.map(renderCard)}</div>}

            {COURSES.flatMap(({ key: courseKey, sections }) => {
              // A closed course collapses to a single banner: its lessons are
              // locked server-side and their bodies withheld, so listing them
              // would only advertise doors that do not open. Admins still see
              // the chapters — that is how the course gets checked before launch.
              if (!config.courses?.[courseKey] && !isAdmin) {
                const count = lessons.filter((l) => sections.some((x) => x.key === l.section)).length;
                return [<ClosedCourse key={courseKey} courseKey={courseKey} lessons={count} />];
              }
              return sections.map(({ key, placeholder }) => {
              const items = lessons.filter((l) => l.section === key);
              if (items.length === 0 && !placeholder) return null;
              const open = openSections.has(key);
              const done = items.filter((l) => l.status === "done" || l.status === "reviewed").length;
              return (
                <div key={key}>
                  <SectionHeader
                    label={t(`study.section${key.charAt(0).toUpperCase()}${key.slice(1)}`)}
                    done={done}
                    total={items.length}
                    open={open}
                    comingSoon={items.length === 0}
                    onToggle={() => toggleSection(key)}
                  />
                  {open && items.length > 0 && (
                    <>
                      {key === PAPER && <div style={{ marginTop: 12 }}><PrintTemplates /></div>}
                      <div className="grid-2 stagger anim-fade" style={{ alignItems: "start", marginTop: 12 }}>
                        {items.map(renderCard)}
                      </div>
                    </>
                  )}
                </div>
              );
              });
            })}
          </>
        )}
      </div>
    </div>
  );
}
